import { createSignaturePad } from '../shared/components/signature-pad';
import {
  isFirmaLegalAccepted,
  renderFirmaLegalBlock,
  updateConfirmSubmitEnabled,
} from '../shared/components/firma-legal-ui';
import { isFirmaDedoEnabled, loadFirmaConfig, syncFirmaDedoUi } from '../shared/config/firma-config';
import type { PrecioTipo } from '../shared/types/autorizacion';
import type { GrainGroup } from '../shared/types/grain';
import type { PendingConfirm } from '../shared/types/venta-form';
import { $ } from '../shared/utils/dom';
import { fmtFechaDia, fmtKg } from '../shared/utils/format';
import { clearVentaNumericFields, initLocaleNumberFields } from '../shared/utils/locale-number-input';
import { postToApp } from '../shared/utils/post-to-app';
import { toast } from '../shared/utils/toast';
import { submitAutorizacion } from '../shared/services/autorizaciones-api';
import {
  AdminGrainBalanceError,
  fetchAppEmbedGrainBalance,
  fetchEmbedTrustedGrainBalance,
} from '../shared/services/grain-balance-api';
import { renderConfirmSummary } from '../venta-form/confirm-summary';
import { EmissionUiController } from '../venta-form/emission-ui';
import {
  renderAccountDisplay,
  renderGrainSelect,
  getSelectedGrainIndex,
  onGrainPickerChange,
  setVencimientoDias,
  syncPrecioTipoUI,
  updateGrainUI,
  validateFormInputs,
} from '../venta-form/form-ui';
import { applyPendingReservationsToCampaigns, buildGrainGroups } from '../venta-form/grain-groups';
import { buildAutorizacionPayload } from '../venta-form/payload';
import {
  pingAppEmbedParentForAuth,
  readEmbedPageQuery,
  resolveAppEmbedAuthContext,
} from '../shared/utils/app-parent-auth';

const DEFAULT_API_BASE = 'https://generico.frogbot.ar/agricola_posse/api';
const SUBMIT_API_URL = '/api/autorizaciones';

type ViewName = 'loading' | 'error' | 'empty' | 'form' | 'confirm' | 'success';

const params = new URLSearchParams(window.location.search);
/** Solo al mismo servidor: un enlace armado no puede llevarse el token del cliente a otro lado. */
function resolverSubmitUrl(valor: string | null): string {
  if (!valor) return SUBMIT_API_URL;
  try {
    return new URL(valor, window.location.origin).origin === window.location.origin ? valor : SUBMIT_API_URL;
  } catch {
    return SUBMIT_API_URL;
  }
}

const submitUrl = resolverSubmitUrl(params.get('submitUrl'));

let token = '';
let accountId = '';
let accountLabel = '';
let apiBase = DEFAULT_API_BASE;

let grains: GrainGroup[] = [];
let precioTipo: PrecioTipo = 'mercado';
let vencimientoDias: number | null = null;
let selectedGrain: GrainGroup | null = null;
let pendingConfirm: PendingConfirm | null = null;

const emission = new EmissionUiController();
const signaturePad = createSignaturePad();

pingAppEmbedParentForAuth(DEFAULT_API_BASE);

function setLoadingMessage(text: string): void {
  const title = $('view-loading')?.querySelector('.state__title');
  if (title) title.textContent = text;
}

function showView(name: ViewName): void {
  (['loading', 'error', 'empty', 'form', 'confirm', 'success'] as const).forEach((v) => {
    $('view-' + v)?.classList.toggle('hidden', v !== name);
  });
  if (name !== 'form') emission.stopPoll();
  if (name === 'form') emission.startPoll(precioTipo, vencimientoDias);
}

function showError(title: string, text: string): void {
  const titleEl = $('error-title');
  const textEl = $('error-text');
  if (titleEl) titleEl.textContent = title;
  if (textEl) textEl.textContent = text;
  showView('error');
}

function goBack(): void {
  postToApp({ code: 'back' });
  if (window.history.length > 1) window.history.back();
}

function getSelectedGrain(): GrainGroup | null {
  return grains[getSelectedGrainIndex()] ?? null;
}

function openConfirmScreen(): void {
  selectedGrain = getSelectedGrain();
  const input = validateFormInputs(selectedGrain, precioTipo, vencimientoDias, emission);
  if (!input || !selectedGrain) return;

  let draft;
  try {
    draft = buildAutorizacionPayload(
      {
        accountId,
        accountLabel,
        apiBase,
        precioTipo,
        selectedGrain,
        emissionSchedule: emission.schedule,
      },
      input.kilos,
      input.precio,
      input.vencimientoDias,
      null
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : 'No se pudo armar la solicitud.';
    toast(message);
    return;
  }

  pendingConfirm = {
    kilos: input.kilos,
    precio: input.precio,
    vencimientoDias: input.vencimientoDias,
    draft,
  };

  renderConfirmSummary(draft, accountId, accountLabel);
  renderFirmaLegalBlock(draft.grano, draft.kilos, () => {
    updateConfirmSubmitEnabled(isFirmaDedoEnabled() ? signaturePad.isEmpty() : false);
  });
  showView('confirm');
  if (isFirmaDedoEnabled()) {
    signaturePad.prepare();
  } else {
    updateConfirmSubmitEnabled(false);
  }
  postToApp({ code: 'setTitle', title: 'Confirmar autorización' });
}

async function handleConfirmSubmit(): Promise<void> {
  if (!pendingConfirm) {
    showView('form');
    return;
  }
  if (isFirmaDedoEnabled() && signaturePad.isEmpty()) {
    toast('Firmá con el dedo antes de enviar.');
    return;
  }
  if (!isFirmaLegalAccepted()) {
    toast('Aceptá la declaración jurada antes de enviar.');
    return;
  }

  const btn = $('btn-confirm-submit') as HTMLButtonElement | null;
  if (!btn) return;
  btn.disabled = true;
  btn.textContent = 'Enviando…';

  selectedGrain = getSelectedGrain();
  if (!selectedGrain) {
    showView('form');
    return;
  }

  let payload;
  try {
    payload = buildAutorizacionPayload(
      {
        accountId,
        accountLabel,
        apiBase,
        precioTipo,
        selectedGrain,
        emissionSchedule: emission.schedule,
      },
      pendingConfirm.kilos,
      pendingConfirm.precio,
      pendingConfirm.vencimientoDias,
      isFirmaDedoEnabled() ? signaturePad.getDataUrl() : null,
      true
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : 'No se pudo armar la solicitud.';
    toast(message);
    btn.disabled = (isFirmaDedoEnabled() && signaturePad.isEmpty()) || !isFirmaLegalAccepted();
    updateConfirmSubmitEnabled(isFirmaDedoEnabled() ? signaturePad.isEmpty() : false);
    return;
  }

  try {
    await submitAutorizacion(submitUrl, payload, {
      authToken: token,
      embedToken: embedSecurityToken || undefined,
    });
    const vtoTxt = payload.fechaVencimiento ? fmtFechaDia(payload.fechaVencimiento) : '—';
    const resumen =
      'Emisión ' +
      fmtFechaDia(emission.schedule?.fechaEmision) +
      ' · ' +
      selectedGrain.grain +
      ' · ' +
      fmtKg(pendingConfirm.kilos) +
      ' kg · ' +
      (precioTipo === 'base'
        ? 'Precio base $' + pendingConfirm.precio + ' · Vto. ' + vtoTxt
        : 'Precio mercado · Vto. ' + vtoTxt);

    pendingConfirm = null;
    const summaryEl = $('success-summary');
    if (summaryEl) summaryEl.textContent = resumen;
    showView('success');
    postToApp({ code: 'showToast', message: 'Autorización de venta registrada', type: 'success' });
    postToApp({ code: 'setTitle', title: 'Autorización registrada' });
  } catch (err) {
    console.error('Error al enviar autorización', err, payload);
    const message = err instanceof Error ? err.message : 'No se pudo enviar la autorización.';
    toast(message);
  } finally {
    btn.disabled = (isFirmaDedoEnabled() && signaturePad.isEmpty()) || !isFirmaLegalAccepted();
    updateConfirmSubmitEnabled(isFirmaDedoEnabled() ? signaturePad.isEmpty() : false);
  }
}

function renderForm(): void {
  clearVentaNumericFields();
  renderAccountDisplay(accountId, accountLabel);
  renderGrainSelect(grains);
  selectedGrain = getSelectedGrain();
  vencimientoDias = syncPrecioTipoUI(precioTipo, vencimientoDias, emission);
  updateGrainUI(selectedGrain);

  void emission.load(precioTipo, vencimientoDias).finally(() => {
    $('venta-form')?.classList.remove('hidden');
    showView('form');
    postToApp({ code: 'ready' });
  });
}

let initSeq = 0;
let embedSecurityToken = '';

async function loadAvailabilityAfterAuth(seq: number, useTrustedEmbed: boolean): Promise<void> {
  setLoadingMessage('Cargando disponibilidad…');
  try {
    const balance = useTrustedEmbed
      ? await fetchEmbedTrustedGrainBalance(accountId, embedSecurityToken)
      : await fetchAppEmbedGrainBalance(accountId, token);
    if (seq !== initSeq) return;

    grains = buildGrainGroups(
      applyPendingReservationsToCampaigns(balance.items, balance.pendingByGrano),
      balance.pendingByGrano
    );
    if (!grains.length) {
      showView('empty');
      return;
    }
    renderForm();
  } catch (err) {
    if (seq !== initSeq) return;
    let message = err instanceof Error ? err.message : String(err);
    if (err instanceof AdminGrainBalanceError && err.code === 'app_token_expired') {
      message = 'Sesión expirada. Cerrá esta pantalla y volvé a abrirla desde la app.';
    } else if (err instanceof AdminGrainBalanceError && err.code === 'embed_token_invalid') {
      message =
        'Token de embed inválido. Revisá que la URL incluya embedToken y coincida con el servidor.';
    } else if (err instanceof AdminGrainBalanceError && err.code === 'embed_token_not_configured') {
      message = 'El servidor aún no tiene configurado EMBED_GRAIN_BALANCE_TOKEN.';
    }
    showError('Error al cargar', message);
  }
}

/** Espera breve al JWT de la app; si no llega, usamos accountId de la URL (app en store sin fix). */
const EMBED_AUTH_WAIT_MS = 5_000;

async function init(): Promise<void> {
  const seq = ++initSeq;
  const embedQuery = readEmbedPageQuery(DEFAULT_API_BASE);
  showView('loading');
  setLoadingMessage('Conectando con la app…');

  pingAppEmbedParentForAuth(DEFAULT_API_BASE);
  const authTask = resolveAppEmbedAuthContext(DEFAULT_API_BASE, { timeoutMs: EMBED_AUTH_WAIT_MS });
  const authSlowHint = window.setTimeout(() => {
    if (seq === initSeq) {
      setLoadingMessage('Esperando sesión de la app…');
    }
  }, 2_000);
  void loadFirmaConfig().then(() => syncFirmaDedoUi());

  let auth: Awaited<ReturnType<typeof resolveAppEmbedAuthContext>>;
  try {
    auth = await authTask;
  } finally {
    window.clearTimeout(authSlowHint);
  }
  if (seq !== initSeq) return;

  const canTrustEmbedAccount =
    embedQuery.embed &&
    /^\d{5}$/.test(embedQuery.accountId) &&
    embedQuery.embedToken.trim().length >= 16;

  if (auth?.token && auth.accountId) {
    token = auth.token;
    accountId = auth.accountId;
    accountLabel = auth.accountLabel;
    apiBase = auth.apiBase;
    await loadAvailabilityAfterAuth(seq, false);
    return;
  }

  if (canTrustEmbedAccount) {
    accountId = embedQuery.accountId;
    accountLabel = embedQuery.accountLabel;
    apiBase = embedQuery.apiBase;
    embedSecurityToken = embedQuery.embedToken.trim();
    token = auth?.token || '';
    await loadAvailabilityAfterAuth(seq, true);
    return;
  }

  const missingEmbedSecret = embedQuery.embed && /^\d{5}$/.test(embedQuery.accountId);
  showError(
    'Faltan parámetros',
    missingEmbedSecret
      ? 'La app no entregó la sesión y falta embedToken en la URL (mismo valor que EMBED_GRAIN_BALANCE_TOKEN del servidor). Agregalo al enlace del iframe cuando puedas.'
      : 'La app no entregó la sesión y falta la cuenta en el enlace. Volvé atrás y abrí de nuevo desde la app con una cuenta seleccionada.'
  );
}

function bindEvents(): void {
  initLocaleNumberFields([
    { id: 'kilos', mode: 'integer' },
    { id: 'precio', mode: 'decimal', maxFractionDigits: 2 },
  ]);

  document.querySelectorAll<HTMLButtonElement>('#precio-segment .segment-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll<HTMLButtonElement>('#precio-segment .segment-btn').forEach((b) => {
        b.classList.toggle('active', b === btn);
      });
      precioTipo = (btn.getAttribute('data-precio') as PrecioTipo) || 'mercado';
      vencimientoDias = syncPrecioTipoUI(precioTipo, vencimientoDias, emission);
    });
  });

  document
    .querySelectorAll<HTMLButtonElement>('#vencimiento-segment .segment-btn--venc')
    .forEach((btn) => {
      btn.addEventListener('click', () => {
        vencimientoDias = setVencimientoDias(
          Number(btn.getAttribute('data-vencimiento')),
          emission,
          precioTipo
        );
      });
    });

  onGrainPickerChange(() => {
    selectedGrain = getSelectedGrain();
    updateGrainUI(selectedGrain);
  });

  $('venta-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    openConfirmScreen();
  });

  $('btn-confirm-back')?.addEventListener('click', () => {
    pendingConfirm = null;
    showView('form');
    postToApp({ code: 'setTitle', title: 'Autorización de venta' });
  });

  $('btn-confirm-submit')?.addEventListener('click', () => {
    void handleConfirmSubmit();
  });

  (['btn-back', 'btn-back-empty'] as const).forEach((id) => {
    $(id)?.addEventListener('click', goBack);
  });

  $('btn-back-error')?.addEventListener('click', goBack);
  $('btn-retry-load')?.addEventListener('click', () => {
    void init();
  });

  $('btn-done')?.addEventListener('click', () => {
    postToApp({ code: 'close' });
    goBack();
  });
}

bindEvents();
void init();
